import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'

export type SignupStatus = 'pending' | 'granted' | 'rejected'

// VRG-247: a comp/activation grant pre-loaded on this waitlist entry, applied
// automatically once a company is created for it.
export type PendingSignupGrants = {
  compDurationMonths?: 3 | 6
  compReason?: string
  activationAmount?: number
  activationReason?: string
  stagedBy: string
  stagedAt: string
}

/**
 * VRG-545: the marketing-site waitlist form's payload, stored verbatim.
 *
 * Mirrors `WaitlistSubmission` in `apps/api/src/domain/repositories/signups.repository.ts`.
 * The index signature is not laziness: the form lives in a separate repo
 * (vera-website) on its own release cadence, so a question added there reaches
 * this row without a platform deploy. Read a named key when you need it; never
 * assume the set is closed.
 *
 * ⚠️ NOTHING RENDERS AN UNRECOGNISED KEY ANY MORE. The detail panel that used to show
 * them was removed once every forwarded field became a column, so a NEW field from
 * vera-website now lands in the row and on no surface at all. That is a deliberate
 * trade — the panel duplicated what the columns already showed — but it means the
 * drift guard (`signups-website-field-coverage.guard.spec.ts`) is now the ONLY thing
 * that surfaces an added field, and its strong half needs vera-website checked out
 * beside this repo. See working-conventions §76. The unrecognised
 * ones too, which is what makes that promise real rather than decorative.
 */
export type WaitlistSubmission = {
  /** Platform product-category labels, e.g. ['Handbags', 'Watches']. */
  categories?: string[]
  /** What the visitor said they want Vera for, e.g. ['Anti-counterfeiting']. */
  solutions?: string[]
  /** The plan the visitor said they were interested in. A free label, not a plan id. */
  planInterest?: string
  source?: string
  /** ISO — when the visitor submitted, as recorded by us on receipt. */
  submittedAt?: string
  /**
   * The request-access form's newsletter box and privacy notice (vera-website, 2026-10-08).
   * Absent on every earlier row, which renders a dash and never "No". Read through
   * `consentFields.ts` on /internal/signups, which documents each key.
   */
  newsletterOptIn?: boolean
  newsletterDefault?: 'ticked' | 'unticked' | 'unknown'
  /** ISO 3166 alpha-2, IP-derived by the site, or ''. */
  country?: string
  privacyNoticeVersion?: string
  /** ISO instant, stamped by the website's server. */
  privacyNoticeShownAt?: string
  [key: string]: unknown
}

/**
 * VRG-545: the marketing-site ROI calculator's submission, stored verbatim.
 *
 * ⚠️ SELF-REPORTED ESTIMATES a prospect typed during a sales evaluation — not
 * measurements, and not commensurable with `MetricInputKey` values despite the
 * similar names. The site's `counterfeitRate` is a share of PRODUCTION, while
 * `company_metric_inputs.counterfeit_conversion_rate` is the share of FAILED
 * VERIFICATIONS that are a genuine counterfeit encounter; `returnFraudRate` is a
 * RATE while `return_fraud_annual_loss` is deliberately a VALUE. Anything
 * rendering these must say whose numbers they are — the columns carry that caveat in
 * each header's info tooltip.
 *
 * ⚠️ THE RATES' UNIT IS ON THE ROW, and must be read rather than assumed.
 * `normalizeAssumptions` converts on ingress and writes `assumptionsUnit: 'ratio'`,
 * so a stored rate is `0.023` for 2.3% and is displayed as a percent by multiplying
 * by 100. Getting that backwards is a 100x misstatement about a stranger's business —
 * the same failure `metricInputFormat.ts` exists to prevent, one surface upstream.
 *
 * ⚠️ THE CURRENCY IS ON THE ROW — read it, never assume dollars. Corrected 2026-09-07
 * (VRG-545): this block used to say the currency was unknowable and that no symbol may
 * ever be rendered. vera-website has since shipped a selector and forwards the chosen ISO
 * 4217 code (`currency`, below), so that instruction now suppresses a fact we hold.
 *
 * ⚠️ IT REMAINS TRUE FOR ROWS WRITTEN BEFORE THE SELECTOR, which carry no code and cannot
 * be backfilled — the unit was never captured. Those fall back to USD, argued at
 * `currencySymbolOf` in `websiteValues.ts`. VRG-577 is company-level CONVERSION, a
 * different subsystem; nothing here converts anything.
 */
export type RoiSubmission = {
  email?: string
  /** Units produced per year. */
  units?: number
  /** Average product price, in the currency the site quoted (major units). */
  price?: number
  /** The calculator's own headline figure, kept so we can tell what it SHOWED them. */
  total?: number
  /**
   * The unit `price`, `cac` and `total` are in — an ISO 4217 CODE, never a symbol.
   *
   * A code because "$" is at least six live currencies and joins to nothing: no rate table,
   * no accounting system and no other column in vera-2 is keyed by glyph, whereas a code
   * read in a year still resolves to exactly one currency.
   *
   * ⚠️ ABSENT ON EVERY ROW PREDATING vera-website's selector, and those rows are of
   * genuinely unknown currency — the site asked for two money figures for months and
   * recorded no unit. Not backfillable. See `currencySymbolOf`.
   *
   * ⚠️ DO NOT SUM OR AVERAGE `price`, `cac` OR `total` ACROSS ROWS WITHOUT GROUPING ON
   * THIS. A mean over mixed currencies is a plausible-looking number with no referent, and
   * nothing about it looks wrong.
   */
  currency?: string
  /** Share of PRODUCTION believed counterfeited — NOT counterfeit_conversion_rate. */
  counterfeitRate?: number
  resaleRate?: number
  resaleValueRatio?: number
  brokerTake?: number
  /** A RATE — NOT return_fraud_annual_loss, which is a value. */
  returnFraudRate?: number
  /**
   * The calculator's sliders, as ONE record — NOT the flat top-level keys above.
   *
   * ⚠️ The five names above (`counterfeitRate` … `returnFraudRate`) are what the API
   * CONTRACT documents; what actually arrives on the wire is this nested object, and
   * the calculator has SEVEN sliders in it, not five. Reading a rate at the top level
   * returns undefined, which renders an em-dash and reads as "the visitor left it
   * blank" — a confusion that has already shipped once on this surface. Read through
   * `assumptions`; `websiteFields.ts` declares which fields are nested.
   *
   * Values are always RATIOS here (`0.023` for 2.3%) — see `assumptionsUnit`.
   */
  assumptions?: Record<string, number>
  /**
   * The unit `assumptions` is stored in. Written as `'ratio'` by
   * `normalizeAssumptions` on ingress, for every row it writes.
   *
   * ⚠️ It is a statement about THIS OBJECT, not about the sender — so it is the thing
   * to read before rendering or comparing a rate, and `'percent'` is the honest
   * reading of a row that predates the field (the site sent percents then).
   */
  assumptionsUnit?: 'percent' | 'ratio'
  /**
   * The pre-conversion copy, kept as evidence. If the website's declared unit is ever
   * wrong, a normalized-only row would have destroyed the only way to find out.
   */
  assumptionsSubmitted?: { unit?: 'percent' | 'ratio'; values?: Record<string, unknown> }
  /**
   * VRG-545 — the ids of sliders the visitor actually MOVED.
   *
   * ⚠️ vera-website DOES send this — corrected 2026-09-07 (VRG-545) after reading its
   * `forwardRoi({...})` call, where `assumptionsTouched` sits between `assumptions` and
   * `industries`. This block previously said nothing sent it. The consequence is that the
   * grid's seeded-default marker is LIVE for rows written by the current site rather than
   * dormant, so a reviewer will start seeing it; rows without the key still mark nothing.
   *
   * It is declared because the alternative — inferring
   * "untouched" by comparing the stored value against the website's seeded default —
   * is a guess dressed as a fact: a visitor who deliberately lands on the default is
   * indistinguishable from one who never touched the slider, and the comparison rots
   * silently the moment the website changes a default.
   *
   * So the grid marks a value as a default ONLY when this list is present and omits it,
   * and marks nothing at all while it is absent. A vera-website change to send it is
   * pending; see `websiteColumns.tsx`.
   */
  assumptionsTouched?: string[]
  source?: string
  /** ISO — when the visitor submitted, as recorded by us on receipt. */
  submittedAt?: string
  [key: string]: unknown
}

/**
 * VRG-658 — "Referred by" on a waitlist row: the signup's live referral, if any. Mirrors
 * `SignupReferralSummary` in `apps/api/src/domain/repositories/signups.repository.ts`. A brand
 * referral names the referring company and the member who sent it; an Insider referral names
 * the FirstPromoter ref. `null` when nobody referred them. Referred PENDING rows are sorted
 * first by the API; none of them skip the queue.
 */
export type SignupReferralSummary = {
  referral_id: string
  kind: 'brand' | 'insider'
  channel: 'link' | 'email_invite' | 'insider'
  status: string
  referrer_company_id: string | null
  referrer_company_name: string | null
  referrer_user_id: string | null
  referrer_user_name: string | null
  referrer_user_email: string | null
  insider_ref: string | null
  created_at: string
}

/**
 * VRG-728 — the marketing site's FIRST RECORDED TOUCH for this lead: the UTMs, referrer HOSTNAME
 * and landing PATH of the visit that submitted, captured for every visitor (no cookie needed).
 * "Recorded" because the site keeps it in memory only, so a reload before submitting loses it.
 */
export type SignupFirstTouch = {
  at: string
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  utm_content: string | null
  utm_term: string | null
  referrer_host: string | null
  landing_path: string | null
}

export type SignupEntry = {
  /** VRG-728 — null when the row never received a touch; absent from an older API. */
  first_touch?: SignupFirstTouch | null
  /** VRG-658 — see `SignupReferralSummary`. Absent if the API's referral join failed. */
  referral?: SignupReferralSummary | null
  id: string
  email: string
  name: string | null
  /**
   * VRG-595 — collected separately by the marketing site since 2026-09-08.
   *
   * ⚠️ NULL on every row predating that, and never backfilled: splitting `name`
   * is wrong exactly where it matters ("van der Berg", "Ana María García
   * Pérez", a mononym), and a fabricated surname is indistinguishable from a
   * real answer. So render `name` as the display name and treat these as the
   * addressable first name when present — do not join them into a display name
   * yourself, because for an older row that produces an empty string next to a
   * perfectly good `name`.
   */
  first_name: string | null
  last_name: string | null
  company_name: string | null
  status: SignupStatus
  source: string | null
  apollo_org_id: string | null
  apollo_person_id: string | null
  company_website: string | null
  created_at: string
  granted_at: string | null
  granted_by_user_id: string | null
  granted_user_id: string | null
  granted_company_id: string | null
  pending_grants: PendingSignupGrants | null
  /** VRG-545: null for admin-created rows. */
  waitlist_submission: WaitlistSubmission | null
  /**
   * VRG-545: null unless this person completed the site's ROI calculator.
   *
   * `roi_submission != null` IS the "ROI submitted" column — derived, never a
   * second boolean field that could disagree with the data it describes.
   */
  roi_submission: RoiSubmission | null
  /**
   * VRG-Pricing.Access.Gate — may this person see vera.inc's real `/pricing`?
   *
   * ⚠️ A DIFFERENT QUESTION from `status`/`granted_at`, which mean "may they
   * create a Vera account". Pricing access is strictly smaller — permission to
   * read a price list — and the two are granted and revoked independently. Do
   * not derive either from the other.
   *
   * LIVE is `pricing_access_granted_at !== null && pricing_access_revoked_at
   * === null`. Read BOTH; a non-null grant alone is not access, it is a record
   * that access was once given. `pricingAccessIsLive` below is the one place
   * that predicate is written down, for the same reason the API has its own
   * copy: a two-condition test written twice will eventually be written two
   * ways, and the silent failure is the dangerous direction (forget the revoke
   * half and a revoked person still looks granted).
   */
  pricing_access_granted_at: string | null
  pricing_access_granted_by_user_id: string | null
  pricing_access_revoked_at: string | null
}

/** VRG-Pricing.Access.Gate — see `SignupEntry.pricing_access_granted_at`. */
export const pricingAccessIsLive = (
  row: Pick<SignupEntry, 'pricing_access_granted_at' | 'pricing_access_revoked_at'>
): boolean => !!row.pricing_access_granted_at && !row.pricing_access_revoked_at

export type SignupsResponse = {
  data: SignupEntry[]
  total: number
}

export const SIGNUPS_QUERY_KEY = 'signups-waitlist'

export type UseSignupsOptions = {
  limit?: number
  offset?: number
  status?: SignupStatus
}

export const useSignups = (options: UseSignupsOptions = {}) => {
  const { limit = 100, offset = 0, status } = options

  const query = useQuery<SignupsResponse>({
    queryKey: [SIGNUPS_QUERY_KEY, limit, offset, status],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('limit', String(limit))
      params.set('offset', String(offset))
      if (status) params.set('status', status)

      const response = await createApiCall({
        method: 'GET',
        url: `/internal/signups?${params.toString()}`,
      })
      return response.data as SignupsResponse
    },
    staleTime: 1000 * 60, // 1 minute
  })

  return {
    signups: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    refetch: query.refetch,
  }
}
